import React from "react";
import * as Icons from "lucide-react";
import { resolvePublicSiteContent, COPYRIGHT_OWNER } from "@/lib/gymSiteDefaults";

function statIcon(name) {
  const key = String(name || "trophy")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("");
  const Icon = Icons[key] || Icons.Trophy;
  return Icon;
}

export function PreviewFrame({ label, children, compact }) {
  return (
    <div className="rounded-sm border border-white/10 overflow-hidden bg-[#0A0A0A]">
      <div className="px-3 py-2 bg-white/5 border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40">
        {label || "Vista previa"}
      </div>
      <div className={compact ? "p-3" : "p-4"}>{children}</div>
    </div>
  );
}

export function BrandFooterPreview({ site }) {
  const s = resolvePublicSiteContent(site);
  return (
    <PreviewFrame label="Marca y pie">
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-2">
          {s.logo ? (
            <img src={s.logo} alt="" className="h-8 w-8 object-cover rounded-sm" />
          ) : (
            <div className="h-8 w-8 bg-[#E63946] text-black font-display grid place-items-center rounded-sm text-sm">B</div>
          )}
          <span className="font-display text-lg text-white">{s.gym_name}</span>
        </div>
        {s.footer_blurb && <p className="text-white/60 text-xs">{s.footer_blurb}</p>}
        <div className="text-xs text-white/40 space-y-0.5">
          {s.address && <div>{s.address}</div>}
          {s.phone && <div>{s.phone}</div>}
          {s.contact_email && <div>{s.contact_email}</div>}
        </div>
        <div className="text-[10px] text-white/40 border-t border-white/10 pt-2">
          © {new Date().getFullYear()} {COPYRIGHT_OWNER}
        </div>
      </div>
    </PreviewFrame>
  );
}

export function HeroPreview({ site }) {
  const s = resolvePublicSiteContent(site);
  const slide = s.hero_carousel_slides?.[0];
  const bg = slide?.type === "image" ? slide.src : null;
  return (
    <PreviewFrame label="Hero">
      <div
        className="relative rounded-sm overflow-hidden min-h-[180px] p-4 flex flex-col justify-end"
        style={bg ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: "#111" }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E63946] mb-2">{s.hero_badge}</div>
          <div className="font-display text-2xl uppercase text-white leading-tight">
            {s.hero_title_prefix}{" "}
            <span className="text-[#E63946]">{s.hero_title_highlight}</span>
            {s.hero_title_suffix ? ` ${s.hero_title_suffix}` : ""}
          </div>
          <p className="text-xs text-white/65 mt-2 line-clamp-3">{s.hero_subtitle}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="text-[10px] px-2 py-1 bg-[#E63946] text-white rounded-sm">{s.hero_btn_primary}</span>
            <span className="text-[10px] px-2 py-1 border border-white/30 text-white/60 rounded-sm">{s.hero_btn_secondary}</span>
          </div>
        </div>
      </div>
    </PreviewFrame>
  );
}

export function StatsPreview({ site }) {
  const s = resolvePublicSiteContent(site);
  const stats = s.home_stats || [];
  return (
    <PreviewFrame label="Estadísticas" compact>
      <div className="grid grid-cols-2 gap-2">
        {stats.slice(0, 4).map((st, i) => {
          const Icon = statIcon(st.icon);
          return (
            <div key={i} className="glass p-2 rounded-sm">
              <Icon className="text-[#FFD700]" size={14} />
              <div className="font-display text-lg text-white">{st.value || "—"}</div>
              <div className="text-[9px] uppercase text-white/60">{st.label || "—"}</div>
            </div>
          );
        })}
      </div>
    </PreviewFrame>
  );
}

export function PackagesSectionPreview({ site }) {
  const s = resolvePublicSiteContent(site);
  return (
    <PreviewFrame label="Sección paquetes" compact>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#E63946]">{s.packages_eyebrow}</div>
      <div className="font-display text-xl text-white mt-1">
        {s.packages_title_prefix} <span className="text-[#FFD700]">{s.packages_title_highlight}</span>
      </div>
      <div className="text-[10px] text-white/40 mt-2">{s.packages_link_label} →</div>
      <div className="mt-3 glass p-2 rounded-sm text-[10px] text-[#FFD700] border border-[#FFD700]/30 inline-block">
        {s.popular_badge_label}
      </div>
    </PreviewFrame>
  );
}

export function EventsSectionPreview({ site }) {
  const s = resolvePublicSiteContent(site);
  return (
    <PreviewFrame label="Sección eventos" compact>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#E63946]">{s.events_eyebrow}</div>
      <div className="font-display text-xl text-white mt-1">
        {s.events_title_prefix} <span className="text-[#FFD700]">{s.events_title_highlight}</span>
      </div>
      <div className="text-[10px] text-white/40 mt-2">{s.events_link_label} →</div>
    </PreviewFrame>
  );
}

export function CtaPreview({ site }) {
  const s = resolvePublicSiteContent(site);
  return (
    <PreviewFrame label="CTA final" compact>
      <div className="font-display text-lg text-white">
        {s.cta_title_prefix} <span className="text-[#E63946]">{s.cta_title_highlight}</span>
        {s.cta_title_suffix || ""}
      </div>
      <p className="text-xs text-white/60 mt-2">{s.cta_subtitle}</p>
      <div className="flex gap-2 mt-3">
        <span className="text-[10px] px-2 py-1 bg-[#FFD700] text-black rounded-sm">{s.cta_btn_primary}</span>
        <span className="text-[10px] px-2 py-1 border border-white/30 text-white/60 rounded-sm">{s.cta_btn_secondary}</span>
      </div>
    </PreviewFrame>
  );
}

export function NavPreview({ site }) {
  const s = resolvePublicSiteContent(site);
  return (
    <PreviewFrame label="Navegación" compact>
      <div className="flex flex-wrap gap-2">
        <span className="text-[10px] border border-white/20 px-2 py-1 text-white/60 rounded-sm">{s.nav_login_label}</span>
        <span className="text-[10px] bg-[#E63946] px-2 py-1 text-white rounded-sm">{s.nav_join_label}</span>
      </div>
    </PreviewFrame>
  );
}

export function PageHeaderPreview({ pageKey, site }) {
  const s = resolvePublicSiteContent(site);
  const p = s[pageKey] || {};
  return (
    <PreviewFrame label={`Página — ${pageKey}`} compact>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#E63946]">{p.eyebrow}</div>
      <div className="font-display text-xl text-white mt-1">
        {p.title_prefix} <span className="text-[#FFD700]">{p.title_highlight}</span>
      </div>
    </PreviewFrame>
  );
}
